import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Card,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
// Api
import {
  GET_TICKET_EVENT_AFFILIATE_SUMMARY,
  GET_TICKET_EVENT_MATCHUPS,
  GET_TICKET_EVENT_PARTICIPANT_INVITE,
  GET_TICKET_EVENT_PEOPLE,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketEventPeople,
  GetTicketEventPeopleVariables,
  GetTicketEventPeople_streamParticipantRows_entities,
} from 'api/ticketing/types/GetTicketEventPeople';
import {
  GetTicketEventParticipantInvite,
  GetTicketEventParticipantInviteVariables,
} from 'api/ticketing/types/GetTicketEventParticipantInvite';
import {
  GetTicketEventAffiliateSummary,
  GetTicketEventAffiliateSummaryVariables,
} from 'api/ticketing/types/GetTicketEventAffiliateSummary';
import {
  GetTicketEventMatchups,
  GetTicketEventMatchupsVariables,
} from 'api/ticketing/types/GetTicketEventMatchups';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import {
  InterviewStatus,
  StreamParticipantRowStatus,
} from 'api/graphql-global-types';
// Hooks
import { useRemoveParticipant } from './hooks/useRemoveParticipant';
// Helpers
import { formatMoney, getPersonName, pluralize } from 'pages/Events/helpers';
import { getEventInviteUrl, getWatchInterviewUrl } from 'pages/Events/links';
// UI
import CopyLink from 'ui/CopyLink/CopyLink';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import KpiCard from 'pages/Events/components/KpiCard/KpiCard';
import AffiliatesCard from '../AffiliatesCard/AffiliatesCard';
// Styles
import styles from './PeopleTab.module.scss';

const { Text, Paragraph } = Typography;

type Participant = GetTicketEventPeople_streamParticipantRows_entities;

const PARTICIPANTS_PAGE_SIZE = 20;

enum InterviewFilter {
  All = 'all',
  Interviewed = 'interviewed',
  NotInterviewed = 'not-interviewed',
}

type PeopleTabProps = {
  event: TicketEventDetailsFields;
};

const PeopleTab = ({ event }: PeopleTabProps): JSX.Element => {
  const [interviewFilter, setInterviewFilter] = useState(InterviewFilter.All);
  const [page, setPage] = useState(1);
  const isStandalone = !event.streamId;
  const owner = isStandalone
    ? { ticketEventId: event.id }
    : { streamId: event.streamId };

  const { data, loading, error, refetch } = useQuery<
    GetTicketEventPeople,
    GetTicketEventPeopleVariables
  >(GET_TICKET_EVENT_PEOPLE, {
    variables: {
      input: { ticketEventId: event.id },
      participantsInput: {
        ...owner,
        statuses: [StreamParticipantRowStatus.Active],
        offset: (page - 1) * PARTICIPANTS_PAGE_SIZE,
        limit: PARTICIPANTS_PAGE_SIZE,
      },
    },
    fetchPolicy: 'cache-and-network',
  });
  // Keeps the KPIs on screen while the next page loads
  const lastData = useRef(data);
  if (data) {
    lastData.current = data;
  }
  const shown = data || lastData.current;
  const { data: inviteData } = useQuery<
    GetTicketEventParticipantInvite,
    GetTicketEventParticipantInviteVariables
  >(GET_TICKET_EVENT_PARTICIPANT_INVITE, {
    variables: { input: { ticketEventId: event.id } },
    skip: !isStandalone,
  });
  const { data: summaryData, refetch: refetchSummary } = useQuery<
    GetTicketEventAffiliateSummary,
    GetTicketEventAffiliateSummaryVariables
  >(GET_TICKET_EVENT_AFFILIATE_SUMMARY, {
    variables: { input: owner },
    fetchPolicy: 'cache-and-network',
  });
  const { data: matchupsData } = useQuery<
    GetTicketEventMatchups,
    GetTicketEventMatchupsVariables
  >(GET_TICKET_EVENT_MATCHUPS, {
    variables: { input: { ticketEventId: event.id } },
    skip: !isStandalone,
  });
  const competitorIds = new Set(
    (matchupsData?.hostedTicketEvent.matchups || []).flatMap((matchup) =>
      (matchup.competitors || []).map(({ storeId }) => storeId)
    )
  );
  const { removeParticipant } = useRemoveParticipant(event.id);

  const analytics = shown?.ticketEventAnalytics;
  const participants = shown?.streamParticipantRows.entities || [];
  const participantsTotal = shown?.streamParticipantRows.total ?? 0;
  const summary = summaryData?.getStreamAffiliateSummary;
  const invite = inviteData?.getOrCreateTicketEventParticipantInvite.invite;

  const interviewByPerson = useMemo(
    () =>
      new Map(
        (analytics?.interviews || [])
          .filter(({ status }) => status === InterviewStatus.Completed)
          .map((interview) => [interview.interviewee.id, interview])
      ),
    [analytics]
  );
  const interviewed = (analytics?.participants || []).filter(
    ({ id }) =>
      interviewFilter === InterviewFilter.All ||
      interviewByPerson.has(id) ===
        (interviewFilter === InterviewFilter.Interviewed)
  );

  const handleRemove = async (participant: Participant): Promise<void> => {
    const removed = await removeParticipant(participant);

    // The last row on a page went, so show the page before it
    if (removed && participants.length === 1 && page > 1) {
      setPage(page - 1);
      return;
    }
    refetch();
  };

  return (
    <div className={styles.root}>
      {!isStandalone && (
        <Alert
          type="info"
          showIcon
          message="This event sells tickets for a stream, so its participants and affiliates are the stream's. Invites and interviews are managed on the stream."
        />
      )}

      {error && (
        <LoadErrorAlert
          message="Failed to load the participants"
          error={error}
        />
      )}

      <KpiCard
        colProps={{ xs: 12, md: 6 }}
        items={[
          {
            title: 'Signed up',
            value: analytics?.participantsCount ?? participantsTotal,
            caption: 'Participants on this event',
          },
          {
            title: 'Interviewed',
            value: analytics?.interviewedCount ?? 0,
            caption: 'Participants who got interviewed',
          },
          {
            title: 'Affiliate earnings',
            value: formatMoney(summary?.affiliateCommissionTotal ?? 0),
            caption: `${pluralize(
              summary?.affiliateTicketsSoldCount ?? 0,
              'ticket'
            )} sold through affiliate links`,
          },
          {
            title: 'Affiliate program',
            value: isStandalone
              ? event.affiliateEnabled
                ? `${event.affiliateCommissionPercent}%`
                : 'Off'
              : 'Stream',
            caption: isStandalone
              ? 'Commission per ticket sale'
              : "Runs on the stream's rate",
          },
        ]}
      />

      <Card title="Participants" className={styles.card}>
        {isStandalone && invite && (
          <div className={styles.invite}>
            <CopyLink
              name="participant-invite"
              label="Participant invitation link"
              link={getEventInviteUrl(event.id, invite)}
            />
            <Text type="secondary">
              Anyone who opens it joins as a participant and gets an affiliate
              link. Removing someone replaces the link.
            </Text>
          </div>
        )}
        <Table<Participant>
          rowKey="id"
          size="small"
          loading={loading && !data}
          dataSource={participants}
          pagination={{
            current: page,
            pageSize: PARTICIPANTS_PAGE_SIZE,
            total: participantsTotal,
            hideOnSinglePage: true,
            onChange: setPage,
          }}
          locale={{ emptyText: 'No participants yet.' }}
          columns={[
            {
              title: 'Participant',
              key: 'name',
              render: (_, participant) => (
                <div className={styles.stack}>
                  <span>{participant.name}</span>
                  {participant.username && (
                    <Text type="secondary">@{participant.username}</Text>
                  )}
                </div>
              ),
            },
            {
              title: '',
              key: 'actions',
              align: 'right' as const,
              render: (_, participant) => {
                if (!isStandalone) {
                  return null;
                }

                if (competitorIds.has(participant.userId)) {
                  return (
                    <Tooltip title="On the matchcard: remove them there.">
                      <Button size="small" disabled>
                        Remove
                      </Button>
                    </Tooltip>
                  );
                }

                return (
                  <Popconfirm
                    title={`Remove ${participant.name}? Their affiliate link stops working.`}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleRemove(participant)}
                  >
                    <Button size="small" danger>
                      Remove
                    </Button>
                  </Popconfirm>
                );
              },
            },
          ]}
        />
        {isStandalone && (
          <Paragraph type="secondary" className={styles.note}>
            Fight card competitors leave through the matchcard.
          </Paragraph>
        )}
      </Card>

      <AffiliatesCard event={event} owner={owner} onChanged={refetchSummary} />

      {isStandalone && (
        <Card
          title="Interviews"
          className={styles.card}
          extra={
            <Radio.Group
              size="small"
              value={interviewFilter}
              onChange={(changeEvent) =>
                setInterviewFilter(changeEvent.target.value)
              }
              optionType="button"
              options={[
                { value: InterviewFilter.All, label: 'All' },
                { value: InterviewFilter.Interviewed, label: 'Interviewed' },
                {
                  value: InterviewFilter.NotInterviewed,
                  label: 'Not interviewed',
                },
              ]}
            />
          }
        >
          <Table
            rowKey="id"
            size="small"
            dataSource={interviewed}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            locale={{ emptyText: 'No participants yet.' }}
            columns={[
              {
                title: 'Participant',
                key: 'name',
                render: (_, person) => (
                  <div className={styles.stack}>
                    <span>{getPersonName(person)}</span>
                    {person.slug && (
                      <Text type="secondary">@{person.slug}</Text>
                    )}
                  </div>
                ),
              },
              {
                title: 'Interview',
                key: 'interview',
                align: 'right' as const,
                render: (_, person) => {
                  const interview = interviewByPerson.get(person.id);
                  const stream = interview?.stream;

                  return stream?.slug && stream.store?.slug ? (
                    <Space>
                      <Text type="success">Interviewed</Text>
                      <Button
                        size="small"
                        href={getWatchInterviewUrl(
                          stream.store.slug,
                          stream.slug
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch
                      </Button>
                    </Space>
                  ) : (
                    <Text type="secondary">Not interviewed</Text>
                  );
                },
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default PeopleTab;
